// Carga das listas auxiliares das Composições (serviços do catálogo, markup base).
//
// Regra da casa: falha de carregamento NUNCA vira "lista vazia" nem "sem
// markup". Cada carga tem o seu estado, e quem usa mostra o erro com "Tentar de
// novo" em vez de concluir que não há nada.
import { useRef, useState } from 'react';

export type EstadoCarga = 'carregando' | 'ok' | 'erro';

// Devolve a função que carrega (e recarrega). Ela nunca rejeita: o resultado
// sai por `aoVir` e `aoMudarEstado`.
// - falhou sem o dado nunca ter vindo → 'erro';
// - falhou numa recarga, com o dado já na tela → fica o que está (velho é
//   melhor que vazio);
// - resposta de um pedido mais antigo que o último é jogada fora.
export function criarCarga<T>(
  buscar: () => Promise<T>,
  aoVir: (dado: T) => void,
  aoMudarEstado: (estado: EstadoCarga) => void,
): () => Promise<void> {
  let pedido = 0;
  let jaVeio = false;
  return async function carregar() {
    const n = ++pedido;
    if (!jaVeio) aoMudarEstado('carregando');
    try {
      const dado = await buscar();
      if (n !== pedido) return;
      jaVeio = true;
      aoVir(dado);
      aoMudarEstado('ok');
    } catch {
      if (n === pedido && !jaVeio) aoMudarEstado('erro');
    }
  };
}

export function useCarga<T>(buscar: () => Promise<T>, inicial: T) {
  const [dado, setDado] = useState<T>(inicial);
  const [estado, setEstado] = useState<EstadoCarga>('carregando');
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;
  // Uma carga só por componente: é ela quem lembra se o dado já veio.
  const [carregar] = useState(() => criarCarga<T>(() => buscarRef.current(), d => setDado(() => d), setEstado));
  return { dado, estado, carregar };
}
